import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, MenuItem, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { Add, Autorenew, Delete, Link as LinkIcon, Payments } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { FileUploadField } from '../../components/FileUploadField';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AmFilterBar, type AmFilters } from '../../components/AmFilterBar';
import { RequestPaymentDialog } from '../../components/RequestPaymentDialog';
import { amAgreementsApi, type AmAgreement, type CreateAgreementInput, type RenewAgreementInput } from '../../api/amAgreements.api';
import { amStationsApi } from '../../api/amStations.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d: string, n: number) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => (s === 'active' ? 'success' : s === 'expiring' ? 'warning' : s === 'expired' ? 'error' : 'default');

export function AgreementsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState<AmFilters>({});
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CreateAgreementInput>({ stationId: 0, startDate: today(), endDate: addDays(today(), 365 * 3), escalationPercent: 5, noticePeriodDays: 30 });
  const [renewRow, setRenewRow] = useState<AmAgreement | null>(null);
  const [renewForm, setRenewForm] = useState<RenewAgreementInput>({});
  const [deleting, setDeleting] = useState<AmAgreement | null>(null);
  const [requestRow, setRequestRow] = useState<AmAgreement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['am-agreements', page, limit, filters],
    queryFn: () => amAgreementsApi.list({ page, limit, status: filters.status || undefined, clientId: filters.clientId || undefined, from: filters.from || undefined, to: filters.to || undefined, expiringInDays: filters.expiringOnly ? 30 : undefined }),
  });
  const { data: stations } = useQuery({ queryKey: ['am-stations', 'all'], queryFn: () => amStationsApi.list({ limit: 500, status: 'active' }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['am-agreements'] });

  const createMut = useMutation({
    mutationFn: () => amAgreementsApi.create(form),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const renewMut = useMutation({
    mutationFn: () => amAgreementsApi.renew(renewRow!.id, renewForm),
    onSuccess: () => { setRenewRow(null); setRenewForm({}); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => amAgreementsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const openRenew = (a: AmAgreement) => {
    const esc = a.escalationPercent ?? 5;
    const start = addDays(a.endDate, 1);
    setRenewForm({
      startDate: start,
      endDate: addDays(start, Math.max(1, Math.round((Date.parse(a.endDate) - Date.parse(a.startDate)) / 86_400_000))),
      monthlyRent: a.monthlyRent != null ? Math.round(a.monthlyRent * (1 + esc / 100) * 100) / 100 : undefined,
      escalationPercent: esc,
    });
    setError(null); setRenewRow(a);
  };

  const columns: Column<AmAgreement>[] = [
    { key: 'agreementNumber', label: 'No.', width: 130 },
    { key: 'stationName', label: 'Site', render: (r) => <span>{r.stationName ?? '—'}<br /><small style={{ color: '#888' }}>{r.city ?? ''}</small></span> },
    { key: 'monthlyRent', label: 'Rent', align: 'right', render: (r) => inr(r.monthlyRent) },
    { key: 'endDate', label: 'Expires', render: (r) => (r.endDate ? new Date(r.endDate).toLocaleDateString('en-IN') : '—') },
    { key: 'daysRemaining', label: 'Days left', render: (r) => (r.daysRemaining == null ? '—' : <span style={{ color: r.daysRemaining <= 30 ? '#d32f2f' : r.daysRemaining <= 60 ? '#ed6c02' : 'inherit', fontWeight: r.daysRemaining <= 30 ? 700 : 400 }}>{r.daysRemaining}</span>) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} /> },
  ];

  return (
    <>
      <PageHeader title="Agreement Management" subtitle="Lease agreements with site owners — renewals, escalation & expiry alerts."
        action={hasPermission('am_agreement.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setFormOpen(true); }}>New Agreement</Button>} />

      <AmFilterBar value={filters} onChange={(f) => { setFilters(f); setPage(1); }} show={['status', 'client', 'dates', 'expiring']} statusOptions={['active', 'expiring', 'expired', 'renewed']} />

      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {r.status !== 'renewed' && hasPermission('am_agreement.renew') && (
              <Tooltip title="Renew"><IconButton size="small" color="primary" onClick={() => openRenew(r)}><Autorenew fontSize="small" /></IconButton></Tooltip>
            )}
            {hasPermission('am_payment.raise') && <Tooltip title="Request Payment (approval)"><IconButton size="small" color="primary" onClick={() => setRequestRow(r)}><Payments fontSize="small" /></IconButton></Tooltip>}
            {r.documentUrl && <Tooltip title="Document"><IconButton size="small" onClick={() => window.open(r.documentUrl!, '_blank')}><LinkIcon fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('am_agreement.update') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      {/* New agreement */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Agreement</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Site *" size="small" value={form.stationId || ''} onChange={(e) => setForm((f) => ({ ...f, stationId: Number(e.target.value) }))}>
              {(stations?.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.stationName} — {s.city ?? ''} (owner: {s.ownerName ?? '—'})</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="Start *" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} />
              <TextField label="End *" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Monthly Rent (₹)" type="number" size="small" fullWidth value={form.monthlyRent ?? ''} onChange={(e) => setForm((f) => ({ ...f, monthlyRent: Number(e.target.value) }))} />
              <TextField label="Escalation %" type="number" size="small" fullWidth value={form.escalationPercent ?? ''} onChange={(e) => setForm((f) => ({ ...f, escalationPercent: Number(e.target.value) }))} />
            </Stack>
            <TextField label="Notice Period (days)" type="number" size="small" value={form.noticePeriodDays ?? ''} onChange={(e) => setForm((f) => ({ ...f, noticePeriodDays: Number(e.target.value) }))} />
            <FileUploadField label="Agreement Document (optional)" accept=".pdf" value={form.documentUrl} onChange={(url) => setForm((f) => ({ ...f, documentUrl: url ?? undefined }))} />
            <Typography variant="caption" color="text.secondary">An expiry reminder is auto-scheduled (notice-period days before end).</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.stationId || !form.endDate || createMut.isPending} onClick={() => { setError(null); createMut.mutate(); }}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Renew */}
      <Dialog open={Boolean(renewRow)} onClose={() => setRenewRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Renew {renewRow?.agreementNumber}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {renewRow && <Alert severity="info">Old rent {inr(renewRow.monthlyRent)} → escalated by {renewForm.escalationPercent ?? renewRow.escalationPercent}%. Adjust below if needed.</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField label="New Start" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={renewForm.startDate ?? ''} onChange={(e) => setRenewForm((f) => ({ ...f, startDate: e.target.value }))} />
              <TextField label="New End" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={renewForm.endDate ?? ''} onChange={(e) => setRenewForm((f) => ({ ...f, endDate: e.target.value }))} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="New Monthly Rent (₹)" type="number" size="small" fullWidth value={renewForm.monthlyRent ?? ''} onChange={(e) => setRenewForm((f) => ({ ...f, monthlyRent: Number(e.target.value) }))} />
              <TextField label="Escalation %" type="number" size="small" fullWidth value={renewForm.escalationPercent ?? ''} onChange={(e) => setRenewForm((f) => ({ ...f, escalationPercent: Number(e.target.value) }))} />
            </Stack>
            <FileUploadField label="Agreement Document (optional)" accept=".pdf" value={renewForm.documentUrl} onChange={(url) => setRenewForm((f) => ({ ...f, documentUrl: url ?? undefined }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewRow(null)}>Cancel</Button>
          <Button variant="contained" disabled={renewMut.isPending} onClick={() => { setError(null); renewMut.mutate(); }}>Renew</Button>
        </DialogActions>
      </Dialog>

      <RequestPaymentDialog
        open={Boolean(requestRow)} onClose={() => setRequestRow(null)}
        entityType="agreement" entityId={requestRow?.id ?? null} title={`Request agreement payment — ${requestRow?.agreementNumber ?? ''}`}
        defaultAmount={requestRow?.monthlyRent ?? undefined}
        onDone={() => { setToast('Payment requested — sent for approval.'); }}
      />
      <ConfirmDialog open={Boolean(deleting)} title="Delete agreement" message={`Delete ${deleting?.agreementNumber}?`} confirmLabel="Delete"
        onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
